import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Award, Loader2, Printer, Share2, ArrowRight, Compass, Copy, Check, Eye, MessageCircle, EyeOff, Send } from "lucide-react";
import { useAuth } from "../context/AuthContext";

/**
 * TrophyCabinet — a wall of finished-adventure certificate miniatures on
 * the Dashboard.  Each miniature is a compact card that opens the full
 * certificate page.  If the learner has finished nothing yet, we show a
 * warm "start your first adventure" prompt instead of the wall.
 */
export default function TrophyCabinet() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [feed, setFeed] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/adventures/progress/me")
      .then(({ data }) => setData(data))
      .catch(() => setData({ completions: [], total: 0, total_available: 0 }));
    api.get("/wall-analytics/me")
      .then(({ data }) => setAnalytics(data))
      .catch(() => setAnalytics(null));
    api.get("/wall-analytics/me/feed?limit=5")
      .then(({ data }) => setFeed(data.days || []))
      .catch(() => setFeed([]));
  }, []);

  const wallHandle = user ? (user.username || (user.email || "").split("@")[0] || user.id) : "";
  const wallUrl = wallHandle && typeof window !== "undefined" ? `${window.location.origin}/wall/${wallHandle}` : "";
  const onCopyWall = async () => {
    if (!wallUrl) return;
    try {
      await navigator.clipboard.writeText(wallUrl);
      setCopied(true);
      toast.success("Wall link copied.");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Couldn't copy."); }
  };

  if (!data) {
    return <div className="rounded-2xl border-2 border-ink bg-cream p-6 text-ink2 text-sm" data-testid="trophy-cabinet-loading">
      <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Dusting the trophy cabinet…
    </div>;
  }

  const completions = data.completions || [];
  const empty = completions.length === 0;

  return (
    <section className="rounded-3xl border-2 border-ink bg-cream shadow-[8px_8px_0_0_#1F1A17] overflow-hidden" data-testid="trophy-cabinet">
      <header className="p-6 md:p-7 border-b-2 border-ink flex items-start gap-4 flex-wrap">
        <div className="w-12 h-12 rounded-2xl bg-marigold/25 text-marigold flex items-center justify-center flex-shrink-0">
          <Award className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-[240px]">
          <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-marigold mb-1">Trophy cabinet</div>
          <h3 className="font-heading text-2xl font-semibold text-ink leading-tight">Your finished expeditions</h3>
          <p className="text-sm text-ink2 mt-1">
            {empty
              ? "No certificates yet — finish an adventure and one appears here."
              : `${data.total} of ${data.total_available} adventures walked.  Each miniature opens the full printable certificate.`}
          </p>
          {analytics && analytics.total_views > 0 && !empty && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.24em] font-bold text-marigold" data-testid="trophy-cabinet-analytics">
              <Eye className="w-3.5 h-3.5" /> {analytics.total_views} view{analytics.total_views === 1 ? "" : "s"} on your wall
              {analytics.recent_views > 0 && analytics.recent_views !== analytics.total_views && (
                <span className="text-ink2/70 tracking-normal normal-case font-normal">· {analytics.recent_views} this week</span>
              )}
            </div>
          )}
        </div>
        {!empty && (
          <div className="flex items-center gap-2 flex-wrap">
            {data.total >= data.total_available && data.total_available > 0 && (
              <Link
                to="/adventures/wrap-up"
                className="btn-primary text-xs inline-flex items-center gap-1.5"
                data-testid="trophy-cabinet-open-wrapup"
              >
                <Award className="w-3.5 h-3.5" /> Open wrap-up scroll
              </Link>
            )}
            {wallHandle && (
              <>
                <Link
                  to={`/wall/${wallHandle}`}
                  className="btn-outline-ink text-xs inline-flex items-center gap-1.5"
                  data-testid="trophy-cabinet-open-wall"
                >
                  <Share2 className="w-3.5 h-3.5" /> Your public wall
                </Link>
                <button
                  onClick={onCopyWall}
                  className="btn-outline-ink text-xs inline-flex items-center gap-1.5"
                  data-testid="trophy-cabinet-copy-wall"
                >
                  {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy link</>}
                </button>
              </>
            )}
            <Link to="/adventures" className="btn-outline-ink text-xs inline-flex items-center gap-1.5" data-testid="trophy-cabinet-more">
              <Compass className="w-3.5 h-3.5" /> More
            </Link>
          </div>
        )}
      </header>

      {empty ? (
        <div className="p-8 md:p-10 text-center" data-testid="trophy-cabinet-empty">
          <div className="font-heading text-lg text-ink mb-2">The wall is bare.</div>
          <p className="text-ink2 text-sm mb-4">Every certificate is hand-typeset by Ms. Pradhan.  Walk an adventure and one arrives.</p>
          <Link to="/adventures" className="btn-primary text-sm inline-flex items-center gap-1.5">
            <Compass className="w-4 h-4" /> Choose your first <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      ) : (
        <div className="p-6 md:p-7">
          {feed.length > 0 && (
            <div className="mb-5 rounded-2xl border border-ink/25 bg-marigold/[0.06] p-4" data-testid="trophy-cabinet-feed">
              <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-marigold mb-2 inline-flex items-center gap-1.5">
                <Eye className="w-3 h-3" /> Recent visits to your wall
              </div>
              <ul className="flex flex-wrap gap-2" data-testid="trophy-cabinet-feed-list">
                {feed.map((d) => (
                  <li
                    key={d.day}
                    className="inline-flex items-center gap-2 rounded-full border border-ink/30 bg-cream px-3 py-1 text-[11px]"
                    data-testid={`trophy-cabinet-feed-${d.day}`}
                  >
                    <span className="font-mono text-ink2/80">{formatFeedDay(d.day)}</span>
                    <span className="font-heading font-semibold text-ink">
                      {d.visitors} visitor{d.visitors === 1 ? "" : "s"}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="mt-2 text-[10px] italic text-ink2/60">
                No names, no IPs — just a warm signal that your wall is being seen.
              </div>
            </div>
          )}
          <GuestbookOwnerPanel />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="trophy-cabinet-grid">
            {completions.map((c) => <TrophyCard key={c.slug} c={c} />)}
          </div>
        </div>
      )}
    </section>
  );
}

function GuestbookOwnerPanel() {
  const [notes, setNotes] = useState(null);
  const [busy, setBusy] = useState("");
  const [replyOpen, setReplyOpen] = useState("");   // note_id currently being replied to
  const [replyText, setReplyText] = useState("");
  const [pending, setPending] = useState({ visitors: 0, pending_replies: 0, recipients: [] });
  const [flushBusy, setFlushBusy] = useState(false);

  const load = () => {
    api.get("/wall-guestbook/me")
      .then(({ data }) => setNotes(data.notes || []))
      .catch(() => setNotes([]));
    api.get("/wall-guestbook/me/pending-digests")
      .then(({ data }) => setPending(data))
      .catch(() => setPending({ visitors: 0, pending_replies: 0, recipients: [] }));
  };
  useEffect(() => { load(); }, []);

  const flushDigests = async () => {
    setFlushBusy(true);
    try {
      const { data } = await api.post("/wall-guestbook/me/flush-digests");
      if (data.sent > 0) {
        toast.success(`Sent ${data.sent} digest${data.sent === 1 ? "" : "s"} on their way.`);
      } else if (data.skipped > 0) {
        toast.info("Email service isn't set up yet — the digest stays queued until it is.");
      } else {
        toast.info("Nothing waiting to send.");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't flush the queue.");
    } finally {
      setFlushBusy(false);
    }
  };

  const toggle = async (n) => {
    if (!n.note_id) return;
    setBusy(n.note_id);
    try {
      const action = n.hidden ? "unhide" : "hide";
      await api.post(`/wall-guestbook/me/${n.note_id}/${action}`);
      setNotes((prev) => prev.map((r) => r.note_id === n.note_id ? { ...r, hidden: !r.hidden } : r));
      toast.success(n.hidden ? "Note is showing again." : "Note hidden from your wall.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update the note.");
    } finally {
      setBusy("");
    }
  };

  const openReply = (n) => {
    setReplyOpen(n.note_id);
    setReplyText(n.owner_reply || "");
  };

  const saveReply = async (n) => {
    setBusy(n.note_id);
    try {
      const { data } = await api.post(`/wall-guestbook/me/${n.note_id}/reply`, { reply: replyText });
      setNotes((prev) => prev.map((r) => r.note_id === n.note_id ? { ...r, owner_reply: data.owner_reply, owner_reply_at: data.owner_reply_at } : r));
      toast.success(data.owner_reply ? "Reply saved." : "Reply cleared.");
      setReplyOpen("");
      setReplyText("");
      // Refresh the pending-digest indicator
      api.get("/wall-guestbook/me/pending-digests")
        .then(({ data }) => setPending(data))
        .catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the reply.");
    } finally {
      setBusy("");
    }
  };

  if (!notes || notes.length === 0) return null;

  return (
    <div className="mb-5 rounded-2xl border border-ink/25 bg-cream p-4" data-testid="trophy-cabinet-guestbook">
      <div className="flex items-center gap-2 mb-3">
        <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-marigold inline-flex items-center gap-1.5">
          <MessageCircle className="w-3 h-3" /> Notes on your wall
        </div>
        <div className="ml-auto text-[10px] font-mono text-ink2/60">
          {notes.filter(n => !n.hidden).length} showing · {notes.filter(n => n.hidden).length} hidden · {notes.filter(n => (n.owner_reply || "").trim()).length} replied
        </div>
      </div>
      {pending.pending_replies > 0 && (
        <div className="mb-3 rounded-xl border border-marigold/50 bg-marigold/[0.08] px-3 py-2 flex items-center gap-3 flex-wrap" data-testid="trophy-cabinet-pending-digest">
          <div className="text-[11px] text-ink leading-tight flex-1 min-w-[200px]">
            <span className="font-heading font-semibold text-ink">{pending.pending_replies} repl{pending.pending_replies === 1 ? "y" : "ies"}</span>
            <span className="text-ink2"> queued for {pending.visitors} visitor{pending.visitors === 1 ? "" : "s"}. Sent as one digest per person.</span>
          </div>
          <button
            onClick={flushDigests}
            disabled={flushBusy}
            className="btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="trophy-cabinet-pending-digest-flush"
          >
            {flushBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Send digests now
          </button>
        </div>
      )}
      <ul className="space-y-2" data-testid="trophy-cabinet-guestbook-list">
        {notes.map((n) => (
          <li
            key={n.note_id || n.created_at}
            className={`rounded-xl border p-3 transition ${n.hidden ? "border-dashed border-ink/25 bg-ink/[0.03] opacity-70" : "border-ink/25 bg-cream hover:border-marigold/60"}`}
            data-testid={`trophy-cabinet-guestbook-note-${n.note_id || n.created_at}`}
          >
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className={`text-sm text-ink font-heading leading-snug ${n.hidden ? "line-through text-ink2" : ""}`}>
                  {n.hidden && <span className="text-[9px] font-mono uppercase tracking-widest text-ink2/60 mr-2">hidden ·</span>}
                  “{n.note}”
                </div>
                <div className="mt-1 text-[10px] uppercase tracking-[0.24em] font-bold text-ink2/60">
                  {n.author_name || "A passer-by"}
                </div>
                {n.owner_reply && replyOpen !== n.note_id && (
                  <div className="mt-2 ml-4 border-l-2 border-marigold pl-3 py-0.5" data-testid={`trophy-cabinet-guestbook-reply-${n.note_id}`}>
                    <div className="text-[9px] uppercase tracking-[0.28em] font-bold text-marigold mb-0.5">Your reply</div>
                    <div className="text-sm text-ink italic font-heading leading-snug">“{n.owner_reply}”</div>
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                {n.note_id && !n.hidden && replyOpen !== n.note_id && (
                  <button
                    onClick={() => openReply(n)}
                    className="text-xs inline-flex items-center gap-1 text-marigold hover:underline"
                    data-testid={`trophy-cabinet-guestbook-reply-open-${n.note_id}`}
                  >
                    <MessageCircle className="w-3.5 h-3.5" /> {n.owner_reply ? "Edit reply" : "Reply"}
                  </button>
                )}
                {n.note_id && (
                  <button
                    onClick={() => toggle(n)}
                    disabled={busy === n.note_id}
                    className={`text-xs inline-flex items-center gap-1 ${n.hidden ? "text-marigold hover:underline" : "text-ink2 hover:text-marigold"}`}
                    data-testid={`trophy-cabinet-guestbook-toggle-${n.note_id}`}
                  >
                    {busy === n.note_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (n.hidden ? <><Eye className="w-3.5 h-3.5" /> Unhide</> : <><EyeOff className="w-3.5 h-3.5" /> Hide</>)}
                  </button>
                )}
              </div>
            </div>
            {replyOpen === n.note_id && (
              <div className="mt-3 ml-4 border-l-2 border-marigold pl-3" data-testid={`trophy-cabinet-guestbook-reply-form-${n.note_id}`}>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value.slice(0, 140))}
                  placeholder="One warm line back…"
                  rows={2}
                  maxLength={140}
                  className="w-full bg-cream border border-ink/25 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-marigold font-heading resize-none"
                  data-testid={`trophy-cabinet-guestbook-reply-textarea-${n.note_id}`}
                  autoFocus
                />
                <div className="mt-2 flex items-center gap-2">
                  <button
                    onClick={() => saveReply(n)}
                    disabled={busy === n.note_id}
                    className="btn-primary text-xs inline-flex items-center gap-1 disabled:opacity-50"
                    data-testid={`trophy-cabinet-guestbook-reply-save-${n.note_id}`}
                  >
                    {busy === n.note_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                    Save reply
                  </button>
                  <button
                    onClick={() => { setReplyOpen(""); setReplyText(""); }}
                    className="text-xs text-ink2 hover:text-ink underline"
                    data-testid={`trophy-cabinet-guestbook-reply-cancel-${n.note_id}`}
                  >
                    Cancel
                  </button>
                  <div className={`ml-auto text-[10px] font-mono ${140 - replyText.length < 20 ? "text-marigold" : "text-ink2/60"}`}>
                    {140 - replyText.length} left
                  </div>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function formatFeedDay(day) {
  if (!day) return "";
  const today = new Date().toISOString().slice(0, 10);
  const yest = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  if (day === today) return "Today";
  if (day === yest) return "Yesterday";
  try {
    return new Date(day + "T00:00:00Z").toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return day; }
}

function TrophyCard({ c }) {
  const dateStr = c.completed_at ? new Date(c.completed_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "";
  const totalCh = (c.act_snapshots || []).reduce((n, a) => n + (a.chapters?.length || 0), 0);
  const roman = (c.kicker || "").split(" ").pop();

  return (
    <div className="group relative" data-testid={`trophy-card-${c.slug}`}>
      <Link
        to={`/adventures/${c.slug}/certificate`}
        className="block relative rounded-2xl border-2 border-ink bg-cream shadow-[5px_5px_0_0_#1F1A17] hover:-translate-y-1 hover:shadow-[8px_8px_0_0_#1F1A17] transition-all overflow-hidden p-5 pb-14"
      >
        {/* Ghost roman */}
        <div className="absolute top-2 right-4 font-heading text-[5rem] font-semibold text-ink/[0.05] leading-none select-none pointer-events-none">
          {roman}
        </div>
        {/* Corner ornaments */}
        <div className="absolute top-2 left-2 w-3 h-3 border-l-2 border-t-2 border-marigold" />
        <div className="absolute bottom-2 right-2 w-3 h-3 border-r-2 border-b-2 border-marigold" />

        <div className="text-[9px] uppercase tracking-[0.28em] font-bold text-marigold mb-2 relative">
          <Award className="w-3 h-3 inline mr-1" /> {c.kicker}
        </div>
        <div className="font-heading text-base font-semibold text-ink leading-snug mb-3 relative pr-8 line-clamp-2 group-hover:text-marigold transition">
          {c.title}
        </div>
        <div className="text-[10px] font-mono text-ink2/70 uppercase tracking-[0.16em] relative">
          {totalCh} chapters · {dateStr}
        </div>

        {/* Ribbon strip */}
        <div className="absolute bottom-0 left-0 right-0 h-9 bg-marigold/10 border-t border-marigold/30 flex items-center justify-between px-4 text-[10px] uppercase tracking-[0.24em] font-bold text-ink2">
          <span className="inline-flex items-center gap-1"><Printer className="w-3 h-3" /> open</span>
          {c.share_token && <span className="inline-flex items-center gap-1"><Share2 className="w-3 h-3" /> shareable</span>}
        </div>
      </Link>
    </div>
  );
}
